import { useCallback, useEffect, useState } from 'react';

import { VisibilityState } from '@tanstack/react-table';

interface UseColumnConfigurationReturn {
  columnVisibility: VisibilityState;
  setColumnVisibility: (visibility: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => void;
  resetToDefaults: () => void;
}

const DEFAULT_VISIBILITY: VisibilityState = {
  // Checked by default
  type: true,
  'file.extension': true,
  issues: true,
  persons: true,
  date: true,
  // Unchecked by default
  author: false,
  factsCount: false,
  'file.createdDate': false,
  'file.relativityMetadata.controlNumber': false,
  'file.relativityMetadata.issues': false,
};

const getStorageKey = (matterId: string) => `wexler_subset_columns_${matterId}`;

/**
 * Custom hook to manage column visibility configuration with localStorage persistence
 * per matter for the subset creator doc-picker-table.
 *
 * @param matterId - The matter ID to scope the configuration
 * @returns Column visibility state and setter functions
 */
export function useColumnConfiguration(matterId: string): UseColumnConfigurationReturn {
  const [columnVisibility, setColumnVisibilityState] = useState<VisibilityState>(() => {
    // Initialize from localStorage if available
    try {
      const stored = localStorage.getItem(getStorageKey(matterId));
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load column configuration from localStorage:', error);
    }
    return DEFAULT_VISIBILITY;
  });

  // Persist to localStorage whenever visibility changes
  useEffect(() => {
    try {
      localStorage.setItem(getStorageKey(matterId), JSON.stringify(columnVisibility));
    } catch (error) {
      console.error('Failed to save column configuration to localStorage:', error);
    }
  }, [matterId, columnVisibility]);

  const setColumnVisibility = useCallback(
    (visibility: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => {
      if (typeof visibility === 'function') {
        setColumnVisibilityState((prev) => visibility(prev));
      } else {
        setColumnVisibilityState(visibility);
      }
    },
    [],
  );

  const resetToDefaults = useCallback(() => {
    setColumnVisibilityState(DEFAULT_VISIBILITY);
  }, []);

  return {
    columnVisibility,
    setColumnVisibility,
    resetToDefaults,
  };
}
